import React, { useEffect } from 'react';
import {
  ArrowLeft,
  Save,
  RefreshCw,
  Info,
  Settings,
  Gift,
  Stamp,
  Coins,
  FileText,
  AlertCircle,
  History,
} from 'lucide-react';
import type { MembershipFormData, MembershipFormErrors } from './membershipFormTypes';
import { formatDateTimeFromApi } from '../../../utils/dateTime';

// FormRow - same style as MembershipCreate
const FormRow: React.FC<{
  label: string;
  required?: boolean;
  children: React.ReactNode;
  error?: string;
}> = ({ label, required, children, error }) => (
  <div className="flex flex-col sm:flex-row sm:items-center gap-3 py-3 border-b border-gray-100 last:border-0">
    <div className="w-52 sm:w-56 flex-shrink-0">
      <p className="text-sm font-medium text-gray-700">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </p>
    </div>
    <div className="flex-1 min-w-0">
      {children}
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  </div>
);

// OptionButtons - same style as MembershipCreate
const OptionButtons: React.FC<{
  options: { value: string; label: string }[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}> = ({ options, value, onChange, disabled }) => (
  <div className="flex flex-wrap gap-2">
    {options.map((opt) => {
      const selected = value === opt.value;
      return (
        <button
          key={opt.value}
          type="button"
          onClick={() => !disabled && onChange(opt.value)}
          disabled={disabled}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            selected ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          } ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
        >
          <span className={`w-2 h-2 rounded-full ${selected ? 'bg-white' : 'bg-gray-400'}`} />
          {opt.label}
        </button>
      );
    })}
  </div>
);

export interface MembershipInfoTabProps {
  mode: 'create' | 'update';
  formData: MembershipFormData;
  setFormData: React.Dispatch<React.SetStateAction<MembershipFormData>>;
  errors: MembershipFormErrors;
  setErrors: React.Dispatch<React.SetStateAction<MembershipFormErrors>>;
  onSubmit: (formData: MembershipFormData) => Promise<void>;
  onReset: () => void;
  isSubmitting: boolean;
  submitError: string | null;
  submitButtonText: string;
  onBackClick: () => void;
  // update mode only
  membershipCode?: string;
  membershipMetadata?: {
    createdAt: string;
    createdBy: string;
    updatedAt: string;
    updatedBy: string;
  };
  hasChanges?: boolean;
}

export const MembershipInfoTab: React.FC<MembershipInfoTabProps> = ({
  mode,
  formData,
  setFormData,
  errors,
  setErrors,
  onSubmit,
  onReset,
  isSubmitting,
  submitError,
  submitButtonText,
  onBackClick,
  membershipCode,
  membershipMetadata,
  hasChanges,
}) => {
  const isRewardMembershipType = formData.type === 'REWARD';

  /** DB 필수 컬럼 대응: 리워드 타입이 아니면 리워드 정책 주체·방식을 고정값으로 둠 */
  useEffect(() => {
    if (!formData.type || formData.type === 'REWARD') return;
    setFormData((prev) => {
      if (prev.rewardPolicyOwner === 'HEADQUARTERS' && prev.rewardType === 'NONE') return prev;
      return { ...prev, rewardPolicyOwner: 'HEADQUARTERS', rewardType: 'NONE' };
    });
  }, [formData.type, setFormData]);

  const numberFields = [
    'paidMembership',
    'membershipLevel',
    'validType',
    'validPeriod',
    'stampSetCount',
    'voucherUseDelay',
    'pointRatio',
    'pointRoundingScale',
  ];

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'number' || numberFields.includes(name) ? Number(value) : value,
    }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validateForm = (): boolean => {
    const newErrors: MembershipFormErrors = {};

    if (!formData.status) newErrors.status = '상태를 선택해주세요.';
    if (!formData.type) newErrors.type = '타입을 선택해주세요.';
    if (!formData.title.trim()) newErrors.title = '타이틀을 입력해주세요.';
    else if (formData.title.length > 20) newErrors.title = '타이틀은 20자 이하로 입력해주세요.';
    if (!formData.startAt || !formData.startAt.trim()) newErrors.startAt = '운영 시작 시각을 입력해주세요.';

    if (formData.type === 'REWARD') {
      if (!formData.rewardPolicyOwner) newErrors.rewardPolicyOwner = '리워드 정책 주체를 선택해주세요.';
      if (!formData.rewardType || formData.rewardType.trim() === '')
        newErrors.rewardType = '리워드 방식을 선택해주세요.';
    }

    if (formData.type === 'REWARD' && (formData.rewardType === 'STAMP' || formData.rewardType === 'POINT')) {
      if (!formData.rewardTitle.trim()) newErrors.rewardTitle = '리워드 시스템 명칭을 입력해주세요.';
      else if (formData.rewardTitle.length > 20)
        newErrors.rewardTitle = '리워드 시스템 명칭은 20자 이하로 입력해주세요.';
      if (!formData.settlementOwner) newErrors.settlementOwner = '정산 주체를 선택해주세요.';
      if (formData.validType === undefined || formData.validType === null)
        newErrors.validType = '유효기간 타입을 선택해주세요.';
      if (formData.validType === 0) {
        if (!formData.validPeriod || formData.validPeriod < 1)
          newErrors.validPeriod = '유효기간을 입력해주세요.';
      } else if (formData.validType === 1) {
        if (!formData.validAt || !formData.validAt.trim())
          newErrors.validAt = '유효일을 입력해주세요.';
      }
    }

    if (formData.type === 'REWARD' && formData.rewardType === 'STAMP') {
      if (!formData.stampSetCount || formData.stampSetCount < 1 || formData.stampSetCount > 100)
        newErrors.stampSetCount = '1세트 스탬프 수는 1-100개 사이여야 합니다.';
      if (!formData.voucherExchangeMethod)
        newErrors.voucherExchangeMethod = '교환권 교환 방식을 선택해주세요.';
      if (
        formData.voucherUseDelay === undefined ||
        formData.voucherUseDelay === null ||
        formData.voucherUseDelay < 0
      )
        newErrors.voucherUseDelay = '교환권 발급 후 사용 딜레이를 입력해주세요.';
    }

    if (formData.type === 'REWARD' && formData.rewardType === 'POINT') {
      if (!formData.pointRewardTarget) newErrors.pointRewardTarget = '포인트 보상 대상을 선택해주세요.';
      if (!formData.pointExpireCycle) newErrors.pointExpireCycle = '포인트 만료 처리 주기를 선택해주세요.';
      if (
        formData.pointRatio === undefined ||
        formData.pointRatio === null ||
        formData.pointRatio < 0 ||
        formData.pointRatio >= 100
      )
        newErrors.pointRatio = '포인트 적립률은 0-99% 사이여야 합니다.';
      if (!formData.pointRoundingMode) newErrors.pointRoundingMode = '포인트 반올림 방식을 선택해주세요.';
      if (
        formData.pointRoundingScale === undefined ||
        formData.pointRoundingScale === null ||
        formData.pointRoundingScale < 1
      )
        newErrors.pointRoundingScale = '포인트 반올림 단위는 1 이상이어야 합니다.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    await onSubmit(formData);
  };

  const statusOptions =
    mode === 'create'
      ? [{ value: 'PENDING', label: '대기중' }]
      : [
          { value: 'ACTIVE', label: '활성' },
          { value: 'INACTIVE', label: '비활성' },
          { value: 'PENDING', label: '대기중' },
          { value: 'TERMINATED', label: '종료' },
        ];

  const typeOptions = [
    { value: 'REWARD', label: '리워드' },
    { value: 'SUBSCRIPTION', label: '구독' },
    { value: 'PASS', label: '패스' },
    { value: 'DISCOUNT', label: '할인' },
  ];

  return (
    <div>
      {/* hasChanges badge (update mode only) */}
      {mode === 'update' && hasChanges && (
        <div className="mb-6">
          <span className="flex items-center text-sm text-orange-600 bg-orange-50 px-3 py-1 rounded-full">
            <AlertCircle className="w-4 h-4 mr-1" />
            수정사항 있음
          </span>
        </div>
      )}

      {/* Membership Info Card (update mode only) */}
      {mode === 'update' && membershipCode && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">
                {formData.title} ({membershipCode})
              </h2>
              {membershipMetadata && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-600">
                  <div className="flex items-center">
                    <History className="w-4 h-4 mr-2" />
                    생성: {formatDateTimeFromApi(membershipMetadata.createdAt) || membershipMetadata.createdAt} ({membershipMetadata.createdBy})
                  </div>
                  <div className="flex items-center">
                    <RefreshCw className="w-4 h-4 mr-2" />
                    수정: {formatDateTimeFromApi(membershipMetadata.updatedAt) || membershipMetadata.updatedAt} ({membershipMetadata.updatedBy})
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {submitError && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertCircle className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">오류가 발생했습니다</h3>
              <div className="mt-2 text-sm text-red-700 whitespace-pre-line">
                <p>{submitError}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* 기본 정보 */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-6">
          <div className="px-4 lg:px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <Info className="w-5 h-5 mr-2" />
              기본 정보
            </h2>
          </div>
          <div className="px-4 lg:px-6 py-2 space-y-0">
            <FormRow label="상태" required error={errors.status}>
              <div className="flex flex-wrap items-center gap-2">
                <OptionButtons
                  options={statusOptions}
                  value={formData.status}
                  onChange={(v) => setFormData((prev) => ({ ...prev, status: v }))}
                  disabled={mode === 'create'}
                />
                {mode === 'create' && (
                  <span className="text-sm text-gray-500">생성 시 상태는 대기중으로 설정됩니다.</span>
                )}
              </div>
            </FormRow>
            <FormRow label="타입" required error={errors.type}>
              <OptionButtons
                options={typeOptions}
                value={formData.type}
                onChange={(v) => {
                  setFormData((prev) => {
                    if (v !== 'REWARD') {
                      return {
                        ...prev,
                        type: v,
                        rewardPolicyOwner: 'HEADQUARTERS',
                        rewardType: 'NONE',
                      };
                    }
                    return { ...prev, type: v };
                  });
                  setErrors((prev) => ({
                    ...prev,
                    type: undefined,
                    ...(v !== 'REWARD'
                      ? {
                          rewardPolicyOwner: undefined,
                          rewardType: undefined,
                          rewardTitle: undefined,
                          settlementOwner: undefined,
                          validType: undefined,
                          validPeriod: undefined,
                          validAt: undefined,
                          stampSetCount: undefined,
                          voucherExchangeMethod: undefined,
                          voucherUseDelay: undefined,
                          pointRewardTarget: undefined,
                          pointExpireCycle: undefined,
                          pointRatio: undefined,
                          pointRoundingMode: undefined,
                          pointRoundingScale: undefined,
                        }
                      : {}),
                  }));
                }}
              />
            </FormRow>
            <FormRow label="타이틀" required error={errors.title}>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                maxLength={20}
                className={`block w-full min-w-[200px] px-3 py-2 border ${errors.title ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm`}
                placeholder="멤버십 타이틀"
              />
            </FormRow>
            <FormRow label="시간대" error={errors.timezone}>
              <input
                type="text"
                name="timezone"
                value={formData.timezone || 'Asia/Seoul'}
                readOnly
                className="block w-full min-w-[200px] px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-600 cursor-not-allowed"
              />
            </FormRow>
            <FormRow label="운영 시작 시각" required error={errors.startAt}>
              <input
                type="datetime-local"
                name="startAt"
                value={formData.startAt}
                onChange={handleInputChange}
                className={`block w-full min-w-[200px] px-3 py-2 border ${errors.startAt ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm`}
              />
            </FormRow>
            <FormRow label="운영 종료 시각" error={errors.endAt}>
              <input
                type="datetime-local"
                name="endAt"
                value={formData.endAt}
                onChange={handleInputChange}
                className={`block w-full min-w-[200px] px-3 py-2 border ${errors.endAt ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm`}
              />
            </FormRow>
          </div>
        </div>

        {/* 멤버십 정책 */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-6">
          <div className="px-4 lg:px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <Settings className="w-5 h-5 mr-2" />
              멤버십 정책
            </h2>
          </div>
          <div className="px-4 lg:px-6 py-2 space-y-0">
            <FormRow label="유료 멤버십" required error={errors.paidMembership}>
              <OptionButtons
                options={[
                  { value: '0', label: '무료' },
                  { value: '1', label: '유료' },
                ]}
                value={String(formData.paidMembership)}
                onChange={(v) => setFormData((prev) => ({ ...prev, paidMembership: Number(v) }))}
              />
            </FormRow>
            <FormRow label="등급제 사용" required error={errors.membershipLevel}>
              <OptionButtons
                options={[
                  { value: '0', label: '미사용' },
                  { value: '1', label: '사용' },
                ]}
                value={String(formData.membershipLevel)}
                onChange={(v) => setFormData((prev) => ({ ...prev, membershipLevel: Number(v) }))}
              />
            </FormRow>
          </div>
        </div>

        {/* 리워드 정책 · 스탬프/포인트 — 멤버십 타입이 리워드일 때만 */}
        {isRewardMembershipType && (
          <>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-6">
          <div className="px-4 lg:px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <Gift className="w-5 h-5 mr-2" />
              리워드 정책
            </h2>
          </div>
          <div className="px-4 lg:px-6 py-2 space-y-0">
            <FormRow label="리워드 정책 주체" required error={errors.rewardPolicyOwner}>
              <OptionButtons
                options={[
                  { value: 'HEADQUARTERS', label: '본사' },
                  { value: 'SHOP', label: '매장' },
                ]}
                value={formData.rewardPolicyOwner}
                onChange={(v) => setFormData((prev) => ({ ...prev, rewardPolicyOwner: v }))}
              />
            </FormRow>
            <FormRow label="리워드 방식" required error={errors.rewardType}>
              <OptionButtons
                options={[
                  { value: 'NONE', label: '없음' },
                  { value: 'STAMP', label: '스탬프' },
                  { value: 'POINT', label: '포인트' },
                ]}
                value={formData.rewardType}
                onChange={(v) => setFormData((prev) => ({ ...prev, rewardType: v }))}
              />
            </FormRow>
          </div>
        </div>

        {/* 리워드 공통 설정 (STAMP / POINT) */}
        {(formData.rewardType === 'STAMP' || formData.rewardType === 'POINT') && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-6">
            <div className="px-4 lg:px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                {formData.rewardType === 'STAMP' ? (
                  <Stamp className="w-5 h-5 mr-2" />
                ) : (
                  <Coins className="w-5 h-5 mr-2" />
                )}
                {formData.rewardType === 'STAMP' ? '스탬프' : '포인트'} 리워드 설정
              </h2>
            </div>
            <div className="px-4 lg:px-6 py-2 space-y-0">
              <FormRow label="리워드 시스템 명칭" required error={errors.rewardTitle}>
                <input
                  type="text"
                  name="rewardTitle"
                  value={formData.rewardTitle}
                  onChange={handleInputChange}
                  maxLength={20}
                  className={`block w-full min-w-[200px] px-3 py-2 border ${errors.rewardTitle ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm`}
                  placeholder="리워드 시스템 명칭"
                />
              </FormRow>
              <FormRow label="정산 주체" required error={errors.settlementOwner}>
                <OptionButtons
                  options={[
                    { value: 'HEADQUARTERS', label: '본사' },
                    { value: 'SHOP', label: '매장' },
                  ]}
                  value={formData.settlementOwner}
                  onChange={(v) => setFormData((prev) => ({ ...prev, settlementOwner: v }))}
                />
              </FormRow>
              <FormRow label="유효기간 타입" required error={errors.validType}>
                <OptionButtons
                  options={[
                    { value: '0', label: '기간' },
                    { value: '1', label: '지정일' },
                  ]}
                  value={String(formData.validType)}
                  onChange={(v) => setFormData((prev) => ({ ...prev, validType: Number(v) }))}
                />
              </FormRow>
              {Number(formData.validType) === 0 && (
                <FormRow label="유효기간 (일 수)" required error={errors.validPeriod}>
                  <input
                    type="number"
                    name="validPeriod"
                    value={formData.validPeriod || ''}
                    onChange={handleInputChange}
                    min={1}
                    className={`block w-full min-w-[120px] px-3 py-2 border ${errors.validPeriod ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]`}
                    placeholder="일 수"
                  />
                </FormRow>
              )}
              {Number(formData.validType) === 1 && (
                <FormRow label="유효일 (지정일)" required error={errors.validAt}>
                  <input
                    type="datetime-local"
                    name="validAt"
                    value={formData.validAt}
                    onChange={handleInputChange}
                    className={`block w-full min-w-[200px] px-3 py-2 border ${errors.validAt ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm`}
                  />
                </FormRow>
              )}
            </div>
          </div>
        )}

        {/* 스탬프 전용 설정 */}
        {formData.rewardType === 'STAMP' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-6">
            <div className="px-4 lg:px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <Stamp className="w-5 h-5 mr-2" />
                스탬프 설정
              </h2>
            </div>
            <div className="px-4 lg:px-6 py-2 space-y-0">
              <FormRow label="1세트(카드 당) 스탬프 수" required error={errors.stampSetCount}>
                <input
                  type="number"
                  name="stampSetCount"
                  value={formData.stampSetCount || ''}
                  onChange={handleInputChange}
                  min={1}
                  max={100}
                  className={`block w-full min-w-[120px] px-3 py-2 border ${errors.stampSetCount ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]`}
                  placeholder="1-100"
                />
              </FormRow>
              <FormRow label="교환권 교환 방식" required error={errors.voucherExchangeMethod}>
                <OptionButtons
                  options={[
                    { value: 'STEP', label: '단계별' },
                    { value: 'CHOICE', label: '선택' },
                  ]}
                  value={formData.voucherExchangeMethod}
                  onChange={(v) => setFormData((prev) => ({ ...prev, voucherExchangeMethod: v }))}
                />
              </FormRow>
              <FormRow label="교환권 발급 후 사용 딜레이 (분)" required error={errors.voucherUseDelay}>
                <input
                  type="number"
                  name="voucherUseDelay"
                  value={formData.voucherUseDelay || ''}
                  onChange={handleInputChange}
                  min={0}
                  className={`block w-full min-w-[120px] px-3 py-2 border ${errors.voucherUseDelay ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]`}
                  placeholder="분 단위"
                />
              </FormRow>
            </div>
          </div>
        )}

        {/* 포인트 전용 설정 */}
        {formData.rewardType === 'POINT' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-6">
            <div className="px-4 lg:px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <Coins className="w-5 h-5 mr-2" />
                포인트 설정
              </h2>
            </div>
            <div className="px-4 lg:px-6 py-2 space-y-0">
              <FormRow label="포인트 보상 대상" required error={errors.pointRewardTarget}>
                <OptionButtons
                  options={[
                    { value: 'PAYMENT', label: '결제금액' },
                    { value: 'PRODUCT', label: '상품' },
                  ]}
                  value={formData.pointRewardTarget}
                  onChange={(v) => setFormData((prev) => ({ ...prev, pointRewardTarget: v }))}
                />
              </FormRow>
              <FormRow label="포인트 만료 처리 주기" required error={errors.pointExpireCycle}>
                <OptionButtons
                  options={[
                    { value: 'YEAR', label: '연' },
                    { value: 'EARLY_MONTH', label: '월초' },
                    { value: 'LATE_MONTH', label: '월말' },
                    { value: 'DAY', label: '일' },
                  ]}
                  value={formData.pointExpireCycle}
                  onChange={(v) => setFormData((prev) => ({ ...prev, pointExpireCycle: v }))}
                />
              </FormRow>
              <FormRow label="포인트 적립률 (%)" required error={errors.pointRatio}>
                <input
                  type="number"
                  name="pointRatio"
                  value={formData.pointRatio || ''}
                  onChange={handleInputChange}
                  min={0}
                  max={99}
                  className={`block w-full min-w-[120px] px-3 py-2 border ${errors.pointRatio ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]`}
                  placeholder="0-99"
                />
              </FormRow>
              <FormRow label="포인트 반올림 방식" required error={errors.pointRoundingMode}>
                <OptionButtons
                  options={[
                    { value: 'HALF_UP', label: '반올림' },
                    { value: 'FLOOR', label: '내림' },
                    { value: 'CEILING', label: '올림' },
                  ]}
                  value={formData.pointRoundingMode}
                  onChange={(v) => setFormData((prev) => ({ ...prev, pointRoundingMode: v }))}
                />
              </FormRow>
              <FormRow label="포인트 반올림 단위" required error={errors.pointRoundingScale}>
                <input
                  type="number"
                  name="pointRoundingScale"
                  value={formData.pointRoundingScale || ''}
                  onChange={handleInputChange}
                  min={1}
                  className={`block w-full min-w-[120px] px-3 py-2 border ${errors.pointRoundingScale ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]`}
                  placeholder="1 이상"
                />
              </FormRow>
            </div>
          </div>
        )}
          </>
        )}

        {/* 기타 설정 */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-6">
          <div className="px-4 lg:px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <FileText className="w-5 h-5 mr-2" />
              기타 설정
            </h2>
          </div>
          <div className="px-4 lg:px-6 py-2 space-y-0">
            <FormRow label="변경사항 기록">
              <textarea
                name="note"
                value={formData.note}
                onChange={handleInputChange}
                rows={4}
                className="block w-full min-w-[200px] px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm resize-y"
                placeholder="변경사항을 기록하세요"
              />
            </FormRow>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between items-center">
          <button
            type="button"
            onClick={onReset}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            초기화
          </button>
          <div className="flex space-x-3">
            <button type="button" onClick={onBackClick} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
              취소
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed flex items-center"
            >
              <Save className="w-5 h-5 mr-2" />
              {isSubmitting ? '저장 중...' : submitButtonText}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default MembershipInfoTab;
